import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { eur, STATUS_LABELS, STATUS_COLORS } from "../../lib/format";
import { ShoppingCart, Package, Users, FileText, Euro, Clock, RotateCcw, MessageSquare, TrendingUp, Award, Ticket, AlertTriangle } from "lucide-react";

const CARDS = [
  { key: "orders_pending", label: "Pedidos pendientes", icon: Clock, color: "text-amber-600" },
  { key: "orders_total", label: "Pedidos totales", icon: ShoppingCart, color: "text-blue-600" },
  { key: "revenue", label: "Facturación (pagado)", icon: Euro, color: "text-emerald-600", money: true },
  { key: "avg_ticket", label: "Ticket medio", icon: TrendingUp, color: "text-cyan-600", money: true },
  { key: "products_total", label: "Productos", icon: Package, color: "text-indigo-600" },
  { key: "customers_total", label: "Clientes", icon: Users, color: "text-purple-600" },
  { key: "invoices_total", label: "Facturas", icon: FileText, color: "text-teal-600" },
  { key: "messages_new", label: "Mensajes nuevos", icon: MessageSquare, color: "text-orange-600" },
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="h-40 bg-secondary animate-pulse rounded-sm" />;

  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tighter mb-6">Dashboard</h1>
      {data.failed_emails?.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-sm p-4" data-testid="failed-emails-alert">
          <p className="font-heading font-semibold text-red-800 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> {data.failed_emails.length} email{data.failed_emails.length === 1 ? "" : "s"} de pedido/factura con problemas en los últimos 7 días</p>
          <ul className="mt-2 space-y-1 text-sm text-red-900">
            {data.failed_emails.slice(0, 5).map((f) => (
              <li key={f.id} className="flex flex-wrap gap-x-2" data-testid={`failed-email-${f.id}`}>
                <span className="font-mono text-xs">{new Date(f.sent_at).toLocaleString("es-ES")}</span>
                <span>{f.to}</span><span className="text-red-700/70">— {f.subject}</span>
                {f.ref && <Link to={`/admin/pedidos?q=${f.ref}`} className="underline" data-testid={`failed-email-link-${f.id}`}>{f.ref}</Link>}
              </li>
            ))}
          </ul>
          <p className="text-xs text-red-700/80 mt-2">Puede reenviarlos desde el historial de emails de cada pedido.</p>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {CARDS.map((c) => (
          <div key={c.key} className="bg-white border border-border rounded-sm p-4" data-testid={`stat-${c.key}`}>
            <c.icon className={`h-6 w-6 ${c.color}`} />
            <p className="font-heading font-bold text-2xl mt-2">{c.money ? eur(data[c.key]) : data[c.key]}</p>
            <p className="text-xs text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6" data-testid="analytics-section">
        <div className="bg-white border border-border rounded-sm p-5 lg:col-span-1">
          <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-brand" /> Ventas por mes</h2>
          {(!data.sales_by_month || data.sales_by_month.length === 0) ? <p className="text-sm text-muted-foreground">Sin ventas pagadas todavía.</p> : (
            <div className="space-y-2">
              {(() => { const max = Math.max(...data.sales_by_month.map((m) => m.total), 1); return data.sales_by_month.map((m) => (
                <div key={m.month} data-testid={`sales-month-${m.month}`}>
                  <div className="flex justify-between text-xs mb-0.5"><span className="font-mono text-muted-foreground">{m.month}</span><span className="font-medium">{eur(m.total)}</span></div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-brand" style={{ width: `${(m.total / max) * 100}%` }} /></div>
                </div>
              )); })()}
            </div>
          )}
        </div>
        <div className="bg-white border border-border rounded-sm p-5">
          <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><Award className="h-5 w-5 text-brand" /> Productos más vendidos</h2>
          {(!data.top_products || data.top_products.length === 0) ? <p className="text-sm text-muted-foreground">Sin datos todavía.</p> : (
            <div className="space-y-2">
              {data.top_products.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm" data-testid={`top-product-${i}`}>
                  <span className="truncate mr-2 flex-1"><span className="font-mono text-muted-foreground mr-1">{i + 1}.</span>{p.name}</span>
                  <span className="font-medium shrink-0">{p.qty} ud · {eur(p.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-border rounded-sm p-5">
          <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><Ticket className="h-5 w-5 text-brand" /> Uso de cupones</h2>
          {(!data.coupons_usage || data.coupons_usage.length === 0) ? <p className="text-sm text-muted-foreground">Ningún cupón usado todavía.</p> : (
            <div className="space-y-2">
              {data.coupons_usage.map((c) => (
                <div key={c.code} className="flex items-center justify-between text-sm" data-testid={`coupon-usage-${c.code}`}>
                  <span className="font-mono font-semibold">{c.code}</span>
                  <span className="text-muted-foreground">{c.orders} pedidos · −{eur(c.discount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border rounded-sm p-5">
          <h2 className="font-heading font-semibold text-lg mb-4">Pedidos por estado</h2>
          <div className="space-y-2">
            {Object.entries(data.by_status).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${STATUS_COLORS[k] || "bg-secondary"}`}>{STATUS_LABELS[k] || k}</span>
                <span className="font-mono font-medium">{v}</span>
              </div>
            ))}
            {Object.values(data.by_status).every((v) => v === 0) && <p className="text-sm text-muted-foreground">Sin pedidos todavía.</p>}
          </div>
        </div>
        <div className="bg-white border border-border rounded-sm p-5">
          <h2 className="font-heading font-semibold text-lg mb-4">Pedidos recientes</h2>
          <div className="space-y-2">
            {data.recent_orders.length === 0 && <p className="text-sm text-muted-foreground">Sin pedidos.</p>}
            {data.recent_orders.map((o) => (
              <Link key={o.id} to={`/admin/pedidos/${o.id}`} className="flex items-center justify-between text-sm py-1.5 hover:text-brand transition-colors" data-testid={`recent-order-${o.number}`}>
                <span className="font-mono">{o.number}</span>
                <span className="text-muted-foreground truncate mx-2 flex-1">{o.customer_name}</span>
                <span className="font-medium">{eur(o.total)}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
